import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import {
    Loader2,
    Save,
    CheckCircle2,
    ClipboardList,
    Droplets,
    Flame,
    Hammer,
    Package,
    Users,
    Calendar,
    Wrench,
    X,
} from "lucide-react";
import { toast } from "sonner";
import {
    computeCrewLadder,
    RATE_TERM_OPTIONS,
    type MarkupTierDraft,
    type ExtinguisherDraft,
    type PricingDraft,
    type RateCardDraftContext,
} from "@/lib/rateCardPreview";
import { validateRateCard } from "@/lib/rateCardValidator";
import { sanitizeMarkupTiers, tiersToApiPayload } from "@/lib/sanitizeMarkupTiers";
import { MarkupTierEditor } from "@/components/rate-card/markup-tier-editor";
import { PasteImporterDialog, type PasteApplyPayload } from "@/components/rate-card/paste-importer-dialog";
import { RateCardSidebar } from "@/components/rate-card/rate-card-sidebar";
import { RateCardSummary } from "@/components/rate-card/rate-card-summary";
import { CustomerRules, type RuleDto } from "@/components/rate-card/customer-rules";
import { CustomerPricingCard } from "@/components/customer-pricing-card";
import { orgTodayKey } from "@/lib/datetime";

type PricingResponse = {
    pricing: PricingDraft;
    rules: RuleDto[];
    markupTiers?: MarkupTierDraft[];
    extinguisherPricing?: ExtinguisherDraft | null;
    trades?: string[];
};

type TradeRow = { id: string; code: string; name: string; is_active: boolean };

function n(v: string | null | undefined): string {
    return v == null || v === "" ? "" : String(v);
}

function numOrNull(v: string | null | undefined): number | null {
    if (v == null || v === "") return null;
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
    return (
        <div>
            <Label>{label}</Label>
            {children}
            {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
        </div>
    );
}

export function CustomerBillingPanel({
    customerId,
    customerName,
    rateCardId,
}: {
    customerId: string;
    customerName?: string;
    rateCardId?: string | null;
}) {
    const qc = useQueryClient();
    const { data, isLoading } = useQuery<PricingResponse>({
        queryKey: ["customer-pricing", customerId],
        queryFn: () => api.get<PricingResponse>(`/api/customers/${customerId}/pricing`),
        enabled: !!customerId,
    });

    const { data: tradesData } = useQuery({
        queryKey: ["trades"],
        queryFn: async () => {
            const res = await api.get<{ trades: TradeRow[] }>("/api/trades");
            return res.trades ?? [];
        },
    });

    const [draft, setDraft] = useState<Partial<PricingDraft>>({});
    const [tiers, setTiers] = useState<MarkupTierDraft[]>([]);
    const [extPricing, setExtPricing] = useState<ExtinguisherDraft | null>(null);
    const [selectedTrades, setSelectedTrades] = useState<string[]>([]);
    const [splitFirelineMain, setSplitFirelineMain] = useState(false);
    const [mode, setMode] = useState<"summary" | "edit">("edit");
    const [termMode, setTermMode] = useState<"preset" | "custom">("preset");
    const userEditingRef = useRef(false);

    const isConfigured = (p: PricingDraft | undefined, trades: string[] | undefined) =>
        p?.billing_mode === "time_and_materials" && (!!p.hourly_labor_rate || (trades?.length ?? 0) > 0);

    useEffect(() => {
        if (data?.pricing) {
            setDraft(data.pricing);
            setSplitFirelineMain(
                !!data.pricing.test_fee_fireline_main &&
                    data.pricing.test_fee_fireline_main !== data.pricing.test_fee_flat,
            );
            if (data.pricing.rate_term_months == null && data.pricing.rate_expires_on) {
                setTermMode("custom");
            } else {
                setTermMode("preset");
            }
            if (isConfigured(data.pricing, data.trades) && !userEditingRef.current) {
                setMode("summary");
            } else if (!isConfigured(data.pricing, data.trades)) {
                setMode("edit");
            }
        }
        if (data?.markupTiers) setTiers(sanitizeMarkupTiers(data.markupTiers));
        if (data?.extinguisherPricing) setExtPricing(data.extinguisherPricing);
        else if (data && !data.extinguisherPricing) setExtPricing(null);
        if (data?.trades) setSelectedTrades(data.trades);
    }, [data]);

    // The fee switches (#1823, permit since migration 700) are plain columns
    // on the customer — not $0 price rules — so they read straight off the
    // pricing payload.
    const cityFeesCharged = (draft.bill_jurisdiction_fees ?? data?.pricing?.bill_jurisdiction_fees) !== false;
    const adminFeeCharged = (draft.charge_admin_fee ?? data?.pricing?.charge_admin_fee) !== false;
    const permitFeeCharged = (draft.charge_permit_fee ?? data?.pricing?.charge_permit_fee) !== false;

    const billingMode = (draft.billing_mode ??
        data?.pricing?.billing_mode ??
        "flat_rate") as PricingDraft["billing_mode"];
    const repairQuoteMode = draft.repair_quote_mode ?? data?.pricing?.repair_quote_mode ?? "pricebook";
    const repairOptionsMode = draft.repair_options_mode ?? data?.pricing?.repair_options_mode ?? "three";
    // NULL is treated as lumped — the migration backfilled every row, so a null
    // here means a customer created since, and one line is the safe default.
    const estimateLineDetail = draft.estimate_line_detail ?? data?.pricing?.estimate_line_detail ?? "lumped";
    const repairLaborRules = data?.pricing?.repair_labor_rules ?? [];
    const isTM = billingMode === "time_and_materials";
    const hasBackflow = selectedTrades.includes("backflow");
    const hasFireExt = selectedTrades.includes("fire_ext");
    const showContractSections = isTM && selectedTrades.length > 0;

    const previewCtx: RateCardDraftContext = useMemo(
        () => ({
            pricing: { ...data?.pricing, ...draft, billing_mode: billingMode },
            tiers,
            extPricing,
            trades: selectedTrades,
            splitFirelineMainFee: splitFirelineMain,
        }),
        [data?.pricing, draft, billingMode, tiers, extPricing, selectedTrades, splitFirelineMain],
    );

    const validationErrors = useMemo(
        () => (isTM ? validateRateCard(previewCtx).filter((l) => l.level === "error") : []),
        [isTM, previewCtx],
    );

    const hourly = numOrNull(draft.hourly_labor_rate ?? data?.pricing?.hourly_labor_rate) ?? 0;
    const crewLadder = computeCrewLadder(hourly);

    const toggleTrade = (code: string) => {
        setSelectedTrades((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
    };

    const buildTermPayload = () => {
        const agreed = draft.rate_agreed_on?.trim() || null;
        const lead = draft.rate_review_lead_days ?? data?.pricing?.rate_review_lead_days ?? 30;
        if (termMode === "custom") {
            return {
                rateAgreedOn: agreed,
                rateTermMonths: null,
                rateExpiresOn: draft.rate_expires_on?.trim() || null,
                rateReviewLeadDays: lead,
            };
        }
        return {
            rateAgreedOn: agreed,
            rateTermMonths: draft.rate_term_months ?? null,
            rateExpiresOn: null,
            rateReviewLeadDays: lead,
        };
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            if (isTM) {
                const errs = validateRateCard(previewCtx).filter((l) => l.level === "error");
                if (errs.length > 0) {
                    throw new Error(errs[0]!.message);
                }
            }
            // Billing mode, labor rate, test price and the fee switches belong
            // to the Pricing card (#1823 S5), which saves them as they change —
            // this form never sends them, so it can't overwrite them.
            const payload: Record<string, unknown> = {
                trades: selectedTrades,
            };
            if (isTM) {
                payload.afterHoursMultiplier = numOrNull(draft.after_hours_multiplier);
                payload.hourlyLaborRateAfterHours = numOrNull(draft.hourly_labor_rate_after_hours);
                payload.testFeeFirelineMain = splitFirelineMain ? numOrNull(draft.test_fee_fireline_main) : null;
                payload.materialsPricingMode = draft.materials_pricing_mode ?? null;
                payload.materialsMarkupPct = numOrNull(draft.materials_markup_pct);
                payload.materialsDiscountPct = numOrNull(draft.materials_discount_pct);
                if (draft.materials_pricing_mode === "tiered_markup") {
                    payload.markupTiers = tiersToApiPayload(tiers);
                }
                payload.extinguisherPricing = extPricing
                    ? {
                          basePrice: Number(extPricing.base_price),
                          baseCount: Number(extPricing.base_count),
                          pricePerAdditional: Number(extPricing.price_per_additional),
                      }
                    : null;
            }
            if (draft.repair_quote_mode !== undefined && draft.repair_quote_mode !== null) {
                payload.repairQuoteMode = draft.repair_quote_mode;
            }
            if (draft.estimate_line_detail !== undefined && draft.estimate_line_detail !== null) {
                payload.estimateLineDetail = draft.estimate_line_detail;
            }
            if (draft.repair_options_mode !== undefined && draft.repair_options_mode !== null) {
                payload.repairOptionsMode = draft.repair_options_mode;
            }
            Object.assign(payload, buildTermPayload());
            await api.patch(`/api/customers/${customerId}/pricing`, payload);
        },
        onSuccess: () => {
            toast.success("Rate card saved");
            userEditingRef.current = false;
            qc.invalidateQueries({ queryKey: ["customer-pricing", customerId] });
            setMode("summary");
        },
        onError: (e: unknown) => {
            const err = e as { message?: string; body?: { message?: string; error?: string } };
            const msg = err.body?.message ?? err.body?.error ?? err.message ?? "unknown";
            toast.error(`Save failed: ${msg}`);
        },
    });

    const renewMutation = useMutation({
        mutationFn: async () => {
            const today = orgTodayKey();
            const p = data?.pricing ?? draft;
            if (termMode === "custom" || (p.rate_term_months == null && p.rate_expires_on)) {
                const oldAgreed = p.rate_agreed_on;
                const oldExpires = p.rate_expires_on;
                let newExpires = oldExpires ?? null;
                if (oldAgreed && oldExpires) {
                    const start = new Date(`${oldAgreed}T12:00:00`);
                    const end = new Date(`${oldExpires}T12:00:00`);
                    const spanDays = Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
                    const d = new Date(`${today}T12:00:00`);
                    d.setDate(d.getDate() + spanDays);
                    newExpires = d.toISOString().slice(0, 10);
                }
                await api.patch(`/api/customers/${customerId}/pricing`, {
                    rateAgreedOn: today,
                    rateTermMonths: null,
                    rateExpiresOn: newExpires,
                    rateReviewLeadDays: p.rate_review_lead_days ?? 30,
                });
            } else {
                await api.patch(`/api/customers/${customerId}/pricing`, {
                    rateAgreedOn: today,
                    rateTermMonths: p.rate_term_months ?? 12,
                    rateExpiresOn: null,
                    rateReviewLeadDays: p.rate_review_lead_days ?? 30,
                });
            }
        },
        onSuccess: () => {
            toast.success("Rate agreement renewed");
            qc.invalidateQueries({ queryKey: ["customer-pricing", customerId] });
        },
        onError: (e: unknown) => {
            const err = e as { message?: string; body?: { message?: string } };
            toast.error(err.body?.message ?? err.message ?? "Renew failed");
        },
    });

    const handleSave = () => {
        if (isTM && validationErrors.length > 0) {
            toast.error(validationErrors[0]!.message);
            return;
        }
        saveMutation.mutate();
    };

    const markComplete = useMutation({
        mutationFn: async () => {
            if (!rateCardId) return;
            await api.patch(`/api/rate-cards/${rateCardId}`, { intakeStatus: "complete" });
        },
        onSuccess: () => {
            toast.success("Rate card marked complete");
            qc.invalidateQueries({ queryKey: ["rate-cards"] });
        },
    });

    const handlePasteApply = (p: PasteApplyPayload) => {
        setDraft((d) => ({ ...d, ...p.draft }));
        if (p.tiers.length) setTiers(p.tiers);
        if (p.trades.length) setSelectedTrades(p.trades);
        setSplitFirelineMain(p.splitFirelineMain);
        // Pasted fee choices go to the Pricing card's switches, which save on
        // their own (#1823 S5) — each to its own switch; the permit is not a
        // city fee (migration 700).
        const feeBody: Record<string, boolean> = {};
        if (p.cityFeesCharged != null) feeBody.billJurisdictionFees = p.cityFeesCharged;
        if (p.permitFeesCharged != null) feeBody.chargePermitFee = p.permitFeesCharged;
        if (p.adminFeeCharged != null) feeBody.chargeAdminFee = p.adminFeeCharged;
        if (Object.keys(feeBody).length > 0) {
            void api
                .patch(`/api/customers/${customerId}/pricing`, feeBody)
                .then(() => {
                    qc.invalidateQueries({ queryKey: ["pricing-profile", customerId] });
                    qc.invalidateQueries({ queryKey: ["customer-pricing", customerId] });
                })
                .catch(() => toast.error("Pasted fee choices NOT saved — set them on the Pricing card"));
        }
        setMode("edit");
        userEditingRef.current = true;
        toast.success("Imported — review fields, then Save rate card");
    };

    if (isLoading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-16 w-full" />
                <div className="grid lg:grid-cols-[1fr_320px] gap-6">
                    <Skeleton className="h-96 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </div>
        );
    }

    if (mode === "summary") {
        return (
            <div className="space-y-5">
                {/* The one Pricing card (#1823 S5): billing, rate, test price,
                    fees, tax, size hours and a typical-quote preview. The
                    sections below are the less-used extras. */}
                <CustomerPricingCard customerId={customerId} customerName={customerName} />
                <div className="rounded-xl border border-border/60 bg-card px-4 py-4 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-semibold tracking-tight">
                                {customerName ? `${customerName} — Rate Card` : "Rate Card"}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-0.5">
                                Contract pricing summary. Pricing remains in effect per agreement terms.
                            </p>
                        </div>
                        {rateCardId && (
                            <Badge variant="outline" className="gap-1">
                                <ClipboardList className="h-3 w-3" /> 2026 intake
                            </Badge>
                        )}
                    </div>
                </div>
                <RateCardSummary
                    ctx={previewCtx}
                    tradesData={tradesData ?? []}
                    cityFeesCharged={cityFeesCharged}
                    adminFeeCharged={adminFeeCharged}
                    permitFeesCharged={permitFeeCharged}
                    splitFirelineMain={splitFirelineMain}
                    onEdit={() => {
                        userEditingRef.current = true;
                        setMode("edit");
                    }}
                    onRenew={() => renewMutation.mutate()}
                    renewPending={renewMutation.isPending}
                />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <CustomerPricingCard customerId={customerId} customerName={customerName} />
            {/* Header strip */}
            <div className="rounded-xl border border-border/60 bg-card px-4 py-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold tracking-tight">
                            {customerName ? `${customerName} — Rate Card` : "Rate Card"}
                        </h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            Contract pricing for this customer. Only configure services you provide.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {isConfigured(data?.pricing, data?.trades) && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    userEditingRef.current = false;
                                    setMode("summary");
                                }}
                            >
                                <X className="h-4 w-4 mr-1" /> Cancel
                            </Button>
                        )}
                        {rateCardId && (
                            <Badge variant="outline" className="gap-1">
                                <ClipboardList className="h-3 w-3" /> 2026 intake
                            </Badge>
                        )}
                        <PasteImporterDialog onApply={handlePasteApply} />
                        {rateCardId && (
                            <>
                                <Button variant="outline" size="sm" asChild>
                                    <Link to="/rate-cards">Roster</Link>
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={() => markComplete.mutate()}
                                    disabled={markComplete.isPending}
                                >
                                    {markComplete.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <>
                                            <CheckCircle2 className="h-4 w-4 mr-1" /> Mark complete
                                        </>
                                    )}
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <p className="text-xs text-muted-foreground mt-3">
                    Billing, labor rate, test price and fees are set on the Pricing card above. These are the extras.
                </p>
            </div>

            <div className="grid lg:grid-cols-[1fr_300px] gap-6 items-start">
                {/* Left column — sections */}
                <div className="space-y-4">
                    {/* HOURLY REPAIR QUOTING RULE: how repair auto-quotes are
                        shaped for this customer — flat pricebook repair items
                        (default) or hourly labor at the profile rate with
                        parts pass-through. */}
                    <Card className="border-border/60">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-sm flex items-center gap-2">
                                <Wrench className="h-4 w-4 text-amber-600" /> Repair quoting
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex rounded-lg border p-1 bg-muted/40 w-fit">
                                {(
                                    [
                                        { value: "pricebook" as const, label: "Pricebook items" },
                                        { value: "hourly" as const, label: "Hourly labor" },
                                    ] as const
                                ).map((opt) => (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        data-testid={`repair-quote-mode-${opt.value}`}
                                        onClick={() => setDraft((d) => ({ ...d, repair_quote_mode: opt.value }))}
                                        className={cn(
                                            "px-4 py-2 rounded-md text-sm font-medium transition-all",
                                            repairQuoteMode === opt.value
                                                ? "bg-background shadow-sm text-foreground"
                                                : "text-muted-foreground hover:text-foreground",
                                        )}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                            {/* PER-DEVICE REPAIR OPTIONS (#1120): how many repair
                                choices this customer is offered for each failed
                                device. Only shown in pricebook mode — hourly
                                customers are quoted labor from their own rules and
                                never get the tier menu, so offering the setting
                                would promise something that cannot happen. */}
                            {repairQuoteMode === "pricebook" && (
                                <div className="space-y-2 border-t pt-3">
                                    <p className="text-xs font-semibold">Repair options per failed device</p>
                                    <div className="flex rounded-lg border p-1 bg-muted/40 w-fit">
                                        {(
                                            [
                                                { value: "three" as const, label: "Show every option" },
                                                { value: "single" as const, label: "Recommended repair only" },
                                            ] as const
                                        ).map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                data-testid={`repair-options-mode-${opt.value}`}
                                                onClick={() =>
                                                    setDraft((d) => ({ ...d, repair_options_mode: opt.value }))
                                                }
                                                className={cn(
                                                    "px-4 py-2 rounded-md text-sm font-medium transition-all",
                                                    repairOptionsMode === opt.value
                                                        ? "bg-background shadow-sm text-foreground"
                                                        : "text-muted-foreground hover:text-foreground",
                                                )}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {repairOptionsMode === "single"
                                            ? "They get one recommended repair per failed device — no menu to work through. Use this for vendors and multi-site accounts, where several rounds of approvals cost everyone time."
                                            : "For each failed device they see every repair we can price — Clean & Flush, rubber kit, complete rebuild — and pick one, or decline that device."}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        This only applies while repair options are switched on in Settings.
                                    </p>
                                </div>
                            )}
                            {/* ESTIMATE LINE DETAIL (#1291 follow-on): whether a
                                replacement reads as its parts or as one line.
                                Shown in every billing mode, unlike the setting
                                above — a time-and-materials account reviews line
                                items just as closely, arguably more so. */}
                            <div className="space-y-2 border-t pt-3">
                                <p className="text-xs font-semibold">Replacement line items</p>
                                <div className="flex rounded-lg border p-1 bg-muted/40 w-fit">
                                    {(
                                        [
                                            { value: "lumped" as const, label: "One line" },
                                            { value: "itemised" as const, label: "Break it out" },
                                        ] as const
                                    ).map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            data-testid={`estimate-line-detail-${opt.value}`}
                                            onClick={() => setDraft((d) => ({ ...d, estimate_line_detail: opt.value }))}
                                            className={cn(
                                                "px-4 py-2 rounded-md text-sm font-medium transition-all",
                                                estimateLineDetail === opt.value
                                                    ? "bg-background shadow-sm text-foreground"
                                                    : "text-muted-foreground hover:text-foreground",
                                            )}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    {estimateLineDetail === "itemised"
                                        ? "A replacement is broken into its parts — assembly, spool piece, flange kit, permit, equipment and labor — each on its own line. Use this for large accounts that review estimates line by line."
                                        : "A replacement is one line, with everything it covers listed in the description."}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Either way the total is the same. This changes how the estimate reads, never what
                                    the customer is charged.
                                </p>
                            </div>
                            {repairQuoteMode === "hourly" ? (
                                <div className="text-xs text-muted-foreground space-y-1.5">
                                    <p>
                                        Repair auto-quotes (failed tests + jurisdiction fail protocols) draft as labor
                                        lines at the hourly rate on this card, plus parts from the pricebook.
                                    </p>
                                    <div className="rounded-md border bg-muted/30 px-3 py-2 space-y-0.5">
                                        {(repairLaborRules.length > 0
                                            ? repairLaborRules
                                            : [{ min_size_in: 0, max_size_in: 1, hours: 2, techs: 1 }]
                                        ).map((r, i) => (
                                            <div key={i} className="font-mono text-[11px]">
                                                {r.min_size_in ?? 0}"–
                                                {r.max_size_in == null ? "any" : `${r.max_size_in}"`} device → {r.hours}{" "}
                                                hr × {r.techs ?? 1} tech
                                            </div>
                                        ))}
                                    </div>
                                    <p>
                                        Device sizes outside these rules draft at the default hours and flag for review.
                                    </p>
                                </div>
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    Repairs quote as flat pricebook repair items (standard behavior).
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {isTM && (
                        <Card className="border-border/60">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Users className="h-4 w-4 text-primary" /> Services provided
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="text-xs text-muted-foreground mb-3">
                                    Select trades this customer receives. Only relevant pricing sections will appear.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {(tradesData ?? []).map((t) => {
                                        const on = selectedTrades.includes(t.code);
                                        return (
                                            <button
                                                key={t.id}
                                                type="button"
                                                onClick={() => toggleTrade(t.code)}
                                                className={cn(
                                                    "px-3 py-1.5 rounded-full text-sm border transition-colors",
                                                    on
                                                        ? "bg-primary text-primary-foreground border-primary"
                                                        : "bg-background border-border hover:border-primary/50",
                                                )}
                                            >
                                                {t.name}
                                            </button>
                                        );
                                    })}
                                </div>
                                {selectedTrades.length === 0 && (
                                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                                        Pick at least one service to configure contract rates.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {showContractSections && hasBackflow && (
                        <Card className="border-border/60">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Droplets className="h-4 w-4 text-sky-600" /> Testing rates
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-xs text-muted-foreground">
                                    The standard test price is on the Pricing card above.
                                </p>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <Checkbox
                                        checked={splitFirelineMain}
                                        onCheckedChange={(v) => setSplitFirelineMain(!!v)}
                                    />
                                    Different rate for fireline mains
                                </label>
                                {splitFirelineMain && (
                                    <Field label="Fireline main test fee ($)">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={n(draft.test_fee_fireline_main)}
                                            onChange={(e) =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    test_fee_fireline_main: e.target.value || null,
                                                }))
                                            }
                                        />
                                    </Field>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {showContractSections && (
                        <Card className="border-border/60">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Hammer className="h-4 w-4 text-orange-600" /> Labor
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                    <Field label="After-hours multiplier">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={n(draft.after_hours_multiplier)}
                                            onChange={(e) =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    after_hours_multiplier: e.target.value || null,
                                                }))
                                            }
                                        />
                                    </Field>
                                    <Field label="After-hours rate ($)" hint="Optional override.">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={n(draft.hourly_labor_rate_after_hours)}
                                            onChange={(e) =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    hourly_labor_rate_after_hours: e.target.value || null,
                                                }))
                                            }
                                        />
                                    </Field>
                                </div>
                                {crewLadder.length > 0 && (
                                    <div className="rounded-lg bg-muted/40 px-3 py-2">
                                        <p className="text-xs font-medium text-muted-foreground mb-1.5">Crew ladder</p>
                                        <div className="flex flex-wrap gap-3 text-sm">
                                            {crewLadder.map((r) => (
                                                <span key={r.crew}>
                                                    {r.crew} tech{r.crew > 1 ? "s" : ""}{" "}
                                                    <strong>${r.rate.toFixed(2)}/hr</strong>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {showContractSections && (
                        <Card className="border-border/60">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Package className="h-4 w-4 text-violet-600" /> Materials
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <Field label="Pricing mode">
                                    <Select
                                        value={draft.materials_pricing_mode ?? "at_list"}
                                        onChange={(e) =>
                                            setDraft((d) => ({
                                                ...d,
                                                materials_pricing_mode: e.target
                                                    .value as PricingDraft["materials_pricing_mode"],
                                            }))
                                        }
                                    >
                                        <option value="at_list">List price</option>
                                        <option value="at_cost_plus">Cost + markup %</option>
                                        <option value="fixed_discount_off_list">Discount off list</option>
                                        <option value="tiered_markup">Tiered cost markup</option>
                                    </Select>
                                </Field>
                                {draft.materials_pricing_mode === "at_cost_plus" && (
                                    <Field label="Markup %">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={n(draft.materials_markup_pct)}
                                            onChange={(e) =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    materials_markup_pct: e.target.value || null,
                                                }))
                                            }
                                        />
                                    </Field>
                                )}
                                {draft.materials_pricing_mode === "fixed_discount_off_list" && (
                                    <Field label="Discount %">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={n(draft.materials_discount_pct)}
                                            onChange={(e) =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    materials_discount_pct: e.target.value || null,
                                                }))
                                            }
                                        />
                                    </Field>
                                )}
                                {draft.materials_pricing_mode === "tiered_markup" && (
                                    <MarkupTierEditor tiers={tiers} onChange={setTiers} />
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {showContractSections && (
                        <Card className="border-border/60">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-primary" /> Rate agreement
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-xs text-muted-foreground">
                                    Optional contract term tracking. Pricing stays in effect until you renew or adjust.
                                </p>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <Field label="Rate agreed on">
                                        <Input
                                            type="date"
                                            value={draft.rate_agreed_on ?? ""}
                                            onChange={(e) =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    rate_agreed_on: e.target.value || null,
                                                }))
                                            }
                                        />
                                    </Field>
                                    <Field label="Term length">
                                        <Select
                                            value={
                                                termMode === "custom" ? "custom" : String(draft.rate_term_months ?? 12)
                                            }
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (v === "custom") {
                                                    setTermMode("custom");
                                                    setDraft((d) => ({ ...d, rate_term_months: null }));
                                                } else {
                                                    setTermMode("preset");
                                                    setDraft((d) => ({
                                                        ...d,
                                                        rate_term_months: Number(v),
                                                        rate_expires_on: null,
                                                    }));
                                                }
                                            }}
                                        >
                                            {RATE_TERM_OPTIONS.map((o) => (
                                                <option key={o.months} value={String(o.months)}>
                                                    {o.label}
                                                </option>
                                            ))}
                                            <option value="custom">Custom end date</option>
                                        </Select>
                                    </Field>
                                </div>
                                {termMode === "custom" && (
                                    <Field label="Custom end date">
                                        <Input
                                            type="date"
                                            value={draft.rate_expires_on ?? ""}
                                            onChange={(e) =>
                                                setDraft((d) => ({
                                                    ...d,
                                                    rate_expires_on: e.target.value || null,
                                                }))
                                            }
                                        />
                                    </Field>
                                )}
                                <Field
                                    label="Review lead time (days)"
                                    hint="Admin is flagged this many days before the term ends."
                                >
                                    <Input
                                        type="number"
                                        min={1}
                                        max={365}
                                        value={
                                            draft.rate_review_lead_days ?? data?.pricing?.rate_review_lead_days ?? 30
                                        }
                                        onChange={(e) =>
                                            setDraft((d) => ({
                                                ...d,
                                                rate_review_lead_days: Number(e.target.value) || 30,
                                            }))
                                        }
                                    />
                                </Field>
                            </CardContent>
                        </Card>
                    )}

                    {showContractSections && hasFireExt && (
                        <Card className="border-border/60">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Flame className="h-4 w-4 text-red-600" /> Extinguishers
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <Field label="Base price ($)">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={extPricing?.base_price ?? ""}
                                            onChange={(e) =>
                                                setExtPricing({
                                                    base_price: e.target.value,
                                                    base_count: extPricing?.base_count ?? 5,
                                                    price_per_additional: extPricing?.price_per_additional ?? "0",
                                                })
                                            }
                                        />
                                    </Field>
                                    <Field label="Included in base (#)">
                                        <Input
                                            type="number"
                                            value={extPricing?.base_count ?? ""}
                                            onChange={(e) =>
                                                setExtPricing({
                                                    base_price: extPricing?.base_price ?? "0",
                                                    base_count: Number(e.target.value) || 0,
                                                    price_per_additional: extPricing?.price_per_additional ?? "0",
                                                })
                                            }
                                        />
                                    </Field>
                                    <Field label="Each additional ($)">
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={extPricing?.price_per_additional ?? ""}
                                            onChange={(e) =>
                                                setExtPricing({
                                                    base_price: extPricing?.base_price ?? "0",
                                                    base_count: extPricing?.base_count ?? 5,
                                                    price_per_additional: e.target.value,
                                                })
                                            }
                                        />
                                    </Field>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {isTM && (
                        <div className="flex justify-end pt-2">
                            <Button
                                onClick={handleSave}
                                disabled={saveMutation.isPending || validationErrors.length > 0}
                                size="lg"
                            >
                                {saveMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4 mr-2" />
                                )}
                                Save rate card
                            </Button>
                        </div>
                    )}

                    {!isTM && (
                        <div className="flex justify-end pt-2">
                            <Button onClick={handleSave} disabled={saveMutation.isPending}>
                                {saveMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                ) : (
                                    <Save className="h-4 w-4 mr-1" />
                                )}
                                Save billing mode
                            </Button>
                        </div>
                    )}

                    {isTM && <CustomerRules customerId={customerId} rules={data?.rules ?? []} defaultCollapsed />}
                </div>

                {/* Right column — preview + validator */}
                <RateCardSidebar ctx={previewCtx} />
            </div>
        </div>
    );
}
